import React, { useState, useEffect } from 'react';
import {
    Container,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Checkbox,
    Button,
    Typography,
    TableContainer,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Paper,
    Fade,
    LinearProgress,
    Stack,
    Box,
    Switch,
    FormControlLabel,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    useMediaQuery,
    useTheme
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAuthContext } from '../context/AuthContext';

import { Track, Series, ReturnData, Artist, Content, Settings } from '../models/TrackInstallVisibility';
import { trackBytes, seriesBytes, selectedContentBytes, formatBytes, headsetStorageIssues, HeadsetStorageIssue } from '../utils/storage';

const deepCopy = <T,>(obj: T): T => {
    return JSON.parse(JSON.stringify(obj));
    // return JSON.parse(JSON.stringify(obj)).content;
};

// Used when the institution has never saved any settings: the menu is off by default
const defaultSettings: Settings = {
    settingsAvailable: false,
};

type Field = 'install' | 'visibility';
type ItemKind = 'track' | 'series';

// Everything that can be provisioned for an artist: standalone tracks, and each
// series as a single item
const artistItems = (artist: Artist): (Track | Series)[] => {
    return [...(artist.tracks ?? []), ...(artist.series ?? [])];
};

const initializeData = (data: Content): Content => {
    const newData = deepCopy(data);
    newData.settings = { ...defaultSettings, ...(newData.settings ?? {}) };
    console.log(typeof (newData));
    // newData.artists.forEach((artist) => {
    //     if (Array.isArray(artist.tracks)) {
    //         artist.tracks.forEach((track) => {
    //             if (track.visibility === undefined) track.visibility = false; // default visibility to false
    //             if (track.install === undefined) track.install = false; // default install to false
    //         });
    //     } else {
    //         artist.tracks = []; // Initialize tracks as an empty array if it's undefined
    //     }
    // });
    return newData;
};

const TrackTable: React.FC = () => {

    // State management for track data:
    // - soapboxData: Raw data from the API
    // - data: Processed and initialized data for rendering and local changes
    // - originalData: A copy of the initial data for comparison
    // - newData: Temporary copy used for updates before setting to 'data'
    //
    // This structure allows for local changes, comparison with original data,
    // and efficient updates without directly mutating the state.
    const { soapboxData, institution, setTrackInstallAndVisibility } = useAuthContext(); 
    const [data, setData] = useState<Content | null>(null);
    const [originalData, setOriginalData] = useState<Content | null>(null);
    const [changesMadeLocally, setChangesMadeLocally] = useState<boolean>(false);
    const [changesInPendingCommit, setChangesInPendingCommit] = useState<boolean>(false);
    // Headsets a rejected save would not have fitted on. Non-empty while the
    // "can't be saved" pop-up is showing.
    const [rejectedSave, setRejectedSave] = useState<HeadsetStorageIssue[]>([]);

    const theme: any = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        if (soapboxData) {
            const initializedData = initializeData(soapboxData);
            setData(initializedData);
            setOriginalData(initializedData);
            // console.log('Initialized data:', initializedData);
            setChangesInPendingCommit(false);
            // console.log("soapbox data updated")
        }
    }, [soapboxData]);

    useEffect(() => {
        // console.log("unsaved changes ", changesInPendingCommit)
    }, [changesInPendingCommit])

    const handleCheckboxChange = (artistId: string, itemId: string, field: Field, kind: ItemKind = 'track') => {
        if (!data) return;
        const newData = deepCopy(data);
        const artist = newData.artists.find((artist) => artist.id === artistId);
        if (artist) {
            const items: (Track | Series)[] = kind === 'series' ? (artist.series ?? []) : artist.tracks;
            const item = items.find((item) => item.id === itemId);
            if (item) {
                item[field] = !item[field];
                if (field === 'install' && !item.install) {
                    item.visibility = false;
                }
                setData(newData);
                setChangesMadeLocally(true);
            }
        }
    };

    const handleArtistCheckboxChange = (artistId: string, checked: boolean, field: Field) => {
        if (!data) return;
        const newData = deepCopy(data);
        const artist = newData.artists.find((artist) => artist.id === artistId);
        if (artist) {
            artistItems(artist).forEach((item) => {
                item[field] = checked;
                if (field === 'install' && !checked) {
                    item.visibility = false;
                }
            });
            setData(newData);
            setChangesMadeLocally(true);
        }
    };

    const handleSettingChange = (field: keyof Settings, checked: boolean) => {
        if (!data) return;
        const newData = deepCopy(data);
        newData.settings = { ...defaultSettings, ...(newData.settings ?? {}), [field]: checked };
        setData(newData);
        setChangesMadeLocally(true);
    };

    const handleRevert = () => {
        if (originalData) {
            setData(deepCopy(originalData));
            setChangesMadeLocally(false);
        }
    };

    const handleSave = () => {
        if (data) {
            // A selection that doesn't fit on every headset is never saved: the
            // changes are thrown away and a pop-up says why.
            const issues = headsetStorageIssues(data, institution?.devices);
            if (issues.length > 0) {
                handleRevert();
                setRejectedSave(issues);
                return;
            }
            const strippedData = stripData(data);
            setOriginalData(deepCopy(data));
            setChangesMadeLocally(false);
            setChangesInPendingCommit(true);
            saveContent(strippedData);
        }
    };

    const stripData = (data: Content): ReturnData => {
        return {
            artists: data.artists.map((artist) => ({
                id: artist.id,
                tracks: artist.tracks.map((track) => ({
                    id: track.id,
                    visibility: track.visibility,
                    install: track.install,
                })),
                // a series only stores install and visibility at the series level
                series: (artist.series ?? []).map((series) => ({
                    id: series.id,
                    visibility: !!series.visibility,
                    install: !!series.install,
                })),
            })),
            settings: { ...defaultSettings, ...(data.settings ?? {}) },
        };
    };

    const saveContent = (data: ReturnData) => {
        setTrackInstallAndVisibility(data);
    };

    const getArtistCheckboxState = (artist: Artist) => {
        const allDownloaded = artistItems(artist).every((item) => item.install);
        const noneDownloaded = artistItems(artist).every((item) => !item.install);
        if (allDownloaded) return true;
        if (noneDownloaded) return false;
        return 'indeterminate';
    };

    const getArtistVisibilityCheckboxState = (artist: Artist) => {
        const allVisible = artistItems(artist).every((item) => item.visibility);
        const noneVisible = artistItems(artist).every((item) => !item.visibility);
        if (allVisible) return true;
        if (noneVisible) return false;
        return 'indeterminate';
    };

    if (!data) {
        return <div>Loading...</div>;
    }

    // Total size of what is currently ticked for download (including unsaved
    // changes), compared with the total storage each headset last reported
    const selectedBytes = selectedContentBytes(data);
    // Headsets the current selection would not fit on (see headsetStorageIssues)
    const storageIssues = headsetStorageIssues(data, institution?.devices);

    return (
        <Container component="main" maxWidth="lg" sx={{ mt: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ width: 'auto' }}>
                <Button
                    variant="contained"
                    color="primary"
                    disabled={!changesMadeLocally || changesInPendingCommit}
                    onClick={handleSave}
                >
                    Save my changes
                </Button>
                <Button
                    variant="outlined"
                    color="primary"
                    disabled={!changesMadeLocally || changesInPendingCommit}
                    onClick={handleRevert}
                >
                    Revert unsaved changes
                </Button>
            </Stack>
            <Fade in={changesInPendingCommit} unmountOnExit>
                <LinearProgress color="primary" sx={{ mt: 1, mb: 1, height: '5px', width: '100%' }} />
            </Fade>
            <Paper sx={{ mt: 2, p: 2 }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={data.settings?.settingsAvailable ?? defaultSettings.settingsAvailable}
                            onChange={(e) => handleSettingChange('settingsAvailable', e.target.checked)}
                            color="primary"
                            disabled={changesInPendingCommit}
                        />
                    }
                    label="Settings available"
                />
                <Typography variant="body2" color="text.secondary">
                    Turn on to make the settings menu available in the Soapbox app on your headsets.
                </Typography>
            </Paper>
            <Paper sx={{ mt: 2, p: 2 }}>
                <Typography variant="body1">
                    Selected for download: <strong>{formatBytes(selectedBytes)}</strong>
                </Typography>
                {storageIssues.length > 0 && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                        This selection does not fit on {storageIssues.length === 1 ? 'this headset' : 'these headsets'} and cannot be saved:{' '}
                        {storageIssues.map((issue) =>
                            `${issue.name} (needs ${formatBytes(issue.neededBytes)}, has room for ${formatBytes(issue.availableBytes)})`).join(', ')}.
                    </Alert>
                )}
            </Paper>
            <Dialog open={rejectedSave.length > 0} onClose={() => setRejectedSave([])}>
                <DialogTitle>Changes not saved</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        The content you selected does not fit on {rejectedSave.length === 1 ? 'this headset' : 'these headsets'}:
                    </DialogContentText>
                    {rejectedSave.map((issue) => (
                        <DialogContentText key={issue.name} sx={{ mt: 1 }}>
                            <strong>{issue.name}</strong>: needs {formatBytes(issue.neededBytes)}, has room for {formatBytes(issue.availableBytes)}
                        </DialogContentText>
                    ))}
                    <DialogContentText sx={{ mt: 2 }}>
                        Nothing was saved and your changes have been reverted. Select less content, or free up
                        space on the headset, and try again.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={() => setRejectedSave([])} autoFocus>OK</Button>
                </DialogActions>
            </Dialog>
            <TableContainer component={Paper} style={{ marginTop: '20px' }}>
                {data.artists.filter((artist) => artistItems(artist).length > 0).map((artist) => (
                    <Accordion key={artist.id} defaultExpanded={false}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls={`panel-${artist.id}-content`}
                            id={`panel-${artist.id}-header`}
                        >
                            <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                <Typography variant="h5">{artist.name}</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', mt: isMobile ? 1 : 0 }}>
                                    <Checkbox
                                        checked={getArtistCheckboxState(artist) === true}
                                        indeterminate={getArtistCheckboxState(artist) === 'indeterminate'}
                                        onChange={(e) => handleArtistCheckboxChange(artist.id, e.target.checked, 'install')}
                                        onClick={(e) => e.stopPropagation()} // Prevent accordion toggle
                                    />
                                    <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Download {artistItems(artist).filter((item) => item.install).length}/{artistItems(artist).length}</Typography>
                                    <Checkbox
                                        checked={getArtistVisibilityCheckboxState(artist) === true}
                                        indeterminate={getArtistVisibilityCheckboxState(artist) === 'indeterminate'}
                                        onChange={(e) => handleArtistCheckboxChange(artist.id, e.target.checked, 'visibility')}
                                        onClick={(e) => e.stopPropagation()} // Prevent accordion toggle
                                        disabled={artistItems(artist).some((item) => !item.install)}
                                    />
                                    <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>Visible {artistItems(artist).filter((item) => item.visibility).length}/{artistItems(artist).length}</Typography>
                                </Box>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Table>
                                <TableHead>
                                    <TableRow key="header-row">
                                        <TableCell key="header-track-name">Track Name</TableCell>
                                        <TableCell key="header-size" sx={{ width: '120px' }}>Size</TableCell>
                                        <TableCell key="header-install" sx={{ width: '150px' }}>Download</TableCell>
                                        <TableCell key="header-visibility" sx={{ width: '150px' }}>Visible</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {artist.tracks.map((track) => (
                                        <TableRow key={`track-${track.id}`}>
                                            <TableCell>{track.title}</TableCell>
                                            <TableCell sx={{ width: '120px' }}>{formatBytes(trackBytes(track))}</TableCell>
                                            <TableCell sx={{ width: '100px' }}>
                                                <Checkbox
                                                    checked={track.install}
                                                    onChange={() =>
                                                        handleCheckboxChange(artist.id, track.id, 'install')
                                                    }
                                                    color="primary"
                                                />
                                            </TableCell>
                                            <TableCell sx={{ width: '100px' }}>
                                                <Checkbox
                                                    checked={track.visibility}
                                                    onChange={() =>
                                                        handleCheckboxChange(artist.id, track.id, 'visibility')
                                                    }
                                                    color="primary"
                                                    disabled={!track.install}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {(artist.series ?? []).map((series) => (
                                        <TableRow key={`series-${series.id}`}>
                                            <TableCell>
                                                {series.title}
                                                <Typography variant="body2" color="text.secondary">
                                                    Series{series.tracks ? ` \u00b7 ${series.tracks.length} tracks` : ''}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ width: '120px' }}>{formatBytes(seriesBytes(series))}</TableCell>
                                            <TableCell sx={{ width: '100px' }}>
                                                <Checkbox
                                                    checked={!!series.install}
                                                    onChange={() =>
                                                        handleCheckboxChange(artist.id, series.id, 'install', 'series')
                                                    }
                                                    color="primary"
                                                />
                                            </TableCell>
                                            <TableCell sx={{ width: '100px' }}>
                                                <Checkbox
                                                    checked={!!series.visibility}
                                                    onChange={() =>
                                                        handleCheckboxChange(artist.id, series.id, 'visibility', 'series')
                                                    }
                                                    color="primary"
                                                    disabled={!series.install}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </AccordionDetails>
                    </Accordion>
                ))}
            </TableContainer>
        </Container >
    );
};

export default TrackTable;